import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Link2Off, PauseCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';

import { SocialAccount } from '../../../../common/models/social-scheduler.model';
import { PlatformIcon, platformConfig } from './PlatformIcon';

interface AffectedPostsResult {
    scheduledCount: number;
    onHoldCount?: number;
    draftCount: number;
    totalCount: number;
}

interface AccountCardProps {
    account: SocialAccount;
    onPause: (uuid: string) => Promise<void>;
    onResume: (uuid: string) => Promise<void>;
    onDisconnect: (uuid: string) => Promise<void>;
    /** Pre-fetched affected post counts */
    getAffectedPosts: (uuid: string) => Promise<AffectedPostsResult>;
    /** Total post count for this account (all statuses) */
    postCount?: number;
}

export const AccountCard: React.FC<AccountCardProps> = ({
    account,
    onPause,
    onResume,
    onDisconnect,
    getAffectedPosts,
    postCount,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isActive = account.status === 'active';
    const isPaused = account.status === 'paused';
    const config = platformConfig[account.platform];
    const platformLabel = config?.label || account.platform;

    // Confirmation dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState<'pause' | 'disconnect' | null>(null);
    const [affectedCounts, setAffectedCounts] = useState<AffectedPostsResult>({ scheduledCount: 0, onHoldCount: 0, draftCount: 0, totalCount: 0 });
    const [actionLoading, setActionLoading] = useState(false);

    // On-hold post count for paused accounts — fetched once on mount/status change
    const [onHoldCount, setOnHoldCount] = useState<number | null>(null);
    useEffect(() => {
        if (!isPaused) {
            setOnHoldCount(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const counts = await getAffectedPosts(account.uuid);
                if (!cancelled) setOnHoldCount(counts.onHoldCount ?? 0);
            } catch {
                if (!cancelled) setOnHoldCount(0);
            }
        })();
        return () => { cancelled = true; };
    }, [isPaused, account.uuid, getAffectedPosts]);

    const handleToggle = async () => {
        if (isActive) {
            // Pause: fetch counts, show confirmation
            try {
                const counts = await getAffectedPosts(account.uuid);
                setAffectedCounts(counts);
                setDialogAction('pause');
                setDialogOpen(true);
            } catch {
                setDialogAction('pause');
                setAffectedCounts({ scheduledCount: 0, draftCount: 0, totalCount: 0 });
                setDialogOpen(true);
            }
        } else if (isPaused) {
            // Resume directly
            await onResume(account.uuid);
        }
    };

    const handleUnlink = async () => {
        try {
            const counts = await getAffectedPosts(account.uuid);
            setAffectedCounts(counts);
        } catch {
            setAffectedCounts({ scheduledCount: 0, draftCount: 0, totalCount: 0 });
        }
        setDialogAction('disconnect');
        setDialogOpen(true);
    };

    const handleConfirm = async () => {
        setActionLoading(true);
        try {
            if (dialogAction === 'pause') {
                await onPause(account.uuid);
            } else if (dialogAction === 'disconnect') {
                await onDisconnect(account.uuid);
            }
        } finally {
            setActionLoading(false);
            setDialogOpen(false);
        }
    };

    return (
        <>
            <div className={cn('rounded-xl border bg-card p-3.5 transition-colors', isPaused && 'opacity-70')}>
                {/* Header row: icon + name + toggle */}
                <div className="mb-2.5 flex items-center gap-2">
                    <PlatformIcon platform={account.platform} size={28} />
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm leading-tight font-medium text-foreground">
                            {account.accountName}
                        </div>
                        <div className="mt-0.5 text-xs leading-none text-muted-foreground">
                            {platformLabel}
                        </div>
                    </div>
                    <Switch
                        checked={isActive}
                        onCheckedChange={handleToggle}
                        disabled={account.status === 'revoked'}
                    />
                </div>

                {/* Status + post count row */}
                <div className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <div className="flex min-w-0 items-center gap-1.5">
                        <span
                            aria-hidden
                            className={cn(
                                'size-1.5 shrink-0 rounded-full',
                                isActive ? 'bg-success' : isPaused ? 'bg-warning' : 'bg-muted-foreground/40',
                            )}
                        />
                        <span className="truncate">
                            {isActive
                                ? t('SOCIAL_SCHEDULER.STATUS_ACTIVE', 'Active')
                                : isPaused
                                    ? t('SOCIAL_SCHEDULER.STATUS_PAUSED', 'Paused')
                                    : t('SOCIAL_SCHEDULER.STATUS_DISCONNECTED', 'Disconnected')}
                        </span>
                    </div>
                    {typeof postCount === 'number' && (
                        <div className="flex shrink-0 items-center gap-1">
                            <span className={cn('font-medium', postCount > 0 ? 'text-foreground' : 'text-muted-foreground')}>
                                {postCount}
                            </span>
                            {postCount === 1
                                ? t('SOCIAL_SCHEDULER.ACCOUNT_POST', 'post')
                                : t('SOCIAL_SCHEDULER.ACCOUNT_POSTS', 'posts')}
                        </div>
                    )}
                </div>

                {/* On-hold posts link (paused accounts) — compact */}
                {isPaused && onHoldCount !== null && onHoldCount > 0 && (
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            navigate(`/marketing/social-scheduler?tab=posts&status=on_hold&accountUuid=${account.uuid}`);
                        }}
                        aria-label={t('SOCIAL_SCHEDULER.VIEW_ON_HOLD_POSTS', `${onHoldCount} posts on hold — click to view`, { count: onHoldCount }) || `${onHoldCount} posts on hold`}
                        className="mb-2 flex w-full items-center justify-between rounded-lg border border-warning/40 bg-warning/10 px-2.5 py-1.5 text-xs font-medium text-warning transition-colors outline-none hover:bg-warning/20 focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                        <span className="flex items-center gap-1.5">
                            <PauseCircle className="size-3.5" />
                            {onHoldCount === 1
                                ? t('SOCIAL_SCHEDULER.ONE_POST_ON_HOLD', '1 post on hold')
                                : t('SOCIAL_SCHEDULER.N_POSTS_ON_HOLD', `${onHoldCount} posts on hold`, { count: onHoldCount })}
                        </span>
                        <ChevronRight className="size-3.5 rtl:rotate-180" />
                    </button>
                )}

                {/* Unlink button — compact */}
                {(isActive || isPaused) && (
                    <Button
                        variant="ghost"
                        size="xs"
                        onClick={handleUnlink}
                        className="-ms-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                        <Link2Off />
                        {t('SOCIAL_SCHEDULER.UNLINK', 'Unlink')}
                    </Button>
                )}
            </div>

            {/* ── Confirmation Dialog ─── */}
            <Dialog open={dialogOpen} onOpenChange={(next) => { if (!next) setDialogOpen(false); }}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>
                            {dialogAction === 'pause'
                                ? t('SOCIAL_SCHEDULER.CONFIRM_PAUSE_TITLE', 'Disable account?')
                                : t('SOCIAL_SCHEDULER.CONFIRM_UNLINK_TITLE', 'Unlink account?')}
                        </DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                        {dialogAction === 'pause' ? (
                            <>
                                {t('SOCIAL_SCHEDULER.CONFIRM_PAUSE_DESC', 'Disabling this account will pause all scheduled posts.')}
                                {affectedCounts.scheduledCount > 0 && (
                                    <span className="mt-3 block font-medium text-foreground">
                                        {t('SOCIAL_SCHEDULER.POSTS_ON_HOLD', {
                                            count: affectedCounts.scheduledCount,
                                            defaultValue: `${affectedCounts.scheduledCount} scheduled post(s) will be put on hold on the calendar.`,
                                        })}
                                    </span>
                                )}
                                {affectedCounts.scheduledCount === 0 && (
                                    <span className="mt-2 block">
                                        {t('SOCIAL_SCHEDULER.NO_POSTS_AFFECTED', 'No scheduled posts will be affected.')}
                                    </span>
                                )}
                            </>
                        ) : (
                            <>
                                {t('SOCIAL_SCHEDULER.CONFIRM_UNLINK_DESC', 'Unlinking this account is permanent. All draft and scheduled posts will be deleted.')}
                                {affectedCounts.totalCount > 0 && (
                                    <span className="mt-3 block font-medium text-destructive">
                                        {affectedCounts.totalCount} {t('SOCIAL_SCHEDULER.POSTS_WILL_BE_DELETED', 'post(s) will be permanently removed from the calendar.')}
                                    </span>
                                )}
                                {affectedCounts.totalCount === 0 && (
                                    <span className="mt-2 block">
                                        {t('SOCIAL_SCHEDULER.NO_POSTS_AFFECTED', 'No posts will be affected.')}
                                    </span>
                                )}
                            </>
                        )}
                    </DialogDescription>
                    <DialogFooter>
                        <Button variant="outline" className="h-9" onClick={() => setDialogOpen(false)}>
                            {t('SOCIAL_SCHEDULER.CANCEL', 'Cancel')}
                        </Button>
                        <Button
                            variant="outline"
                            disabled={actionLoading}
                            onClick={handleConfirm}
                            className={cn(
                                'h-9',
                                dialogAction === 'disconnect' &&
                                    'border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive',
                            )}
                        >
                            {actionLoading
                                ? '...'
                                : dialogAction === 'pause'
                                    ? t('SOCIAL_SCHEDULER.CONFIRM_DISABLE', 'Disable')
                                    : t('SOCIAL_SCHEDULER.CONFIRM_UNLINK', 'Unlink')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
